import { Anchor } from "@sellar-io/components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { formatCurrency } from "@sellar-io/common";
import * as ROUTES from "../../../../../constants/routes";
import { Icon, SpinnerLegacy } from "../../../../../components";
import type {
  InvoicePayoutType,
  CreditNotePayoutType,
  InvoiceNextPayoutType,
} from "@sellar-io/api-v2/types";
import { type DocumentType } from "../../stores/PayoutFilterStore";
import { Dot } from "../../Components/SellarCreditStats";
import { useSearchParams } from "react-router";
import { Button, H5 } from "@sellar-io/components";
import { TanStackTableSimple } from "@sellar-io/components/adapters";
import { useSellarImportPayout } from "../hooks/useSellarImportPayout";
import { StatusBadge } from "./StatusBadge";
import { SellarImportPayoutDocumentSummary } from "./SellarImportPayoutDocumentSummary";
import { AdminOnly } from "../../../../../components/authFilter/AdminOnly";
import { formatDate } from "../../../../../utils/formatDate";
import { UpdateSellarPayoutDialog } from "./UpdateSellarPayoutDialog";
import { AddCustomFeeDialog } from "./AddCustomFeeDialog";
import { useSellarImportCustomFeeRemove } from "../hooks/useSellarImportCustomFeeRemove";

type TableRow = {
  id: number;
  orderId: number | null;
  customerBusinessName: string;
  total: number;
  type: "order" | "creditNote" | "customFee";
  createdAt: Date;
  paidAt: Date | null;
};

export type SellarCreditDocument = {
  type: DocumentType;
  contents: InvoicePayoutType | CreditNotePayoutType | InvoiceNextPayoutType;
};

function PayoutDetailsTable({
  selectedPayoutId,
  onHide,
}: {
  selectedPayoutId: number;
  onHide: () => void;
}) {
  const [updatePayoutDialogActive, setUpdatePayoutDialogActive] =
    useState(false);
  const [addCustomFeeDialogActive, setAddCustomFeeDialogActive] =
    useState(false);

  const sellarImportPayoutQuery = useSellarImportPayout({
    id: selectedPayoutId,
  });

  const sellarImportPayout = sellarImportPayoutQuery.data;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const removeCustomFeeMutation = useSellarImportCustomFeeRemove({
    payoutId: selectedPayoutId,
  });

  const data = useMemo<TableRow[]>(() => {
    if (!sellarImportPayout) return [];

    const orderRows = sellarImportPayout.Orders.map((order) => {
      return {
        id: order.id,
        orderId: order.id,
        total: order.priceExVat,
        customerBusinessName: order.Retailer.Business.name,
        type: "order" as const,
        createdAt: order.createdAt,
        paidAt: order.paidAt,
      };
    });
    const creditNoteRows = sellarImportPayout.CreditNotes.map((creditNote) => {
      return {
        id: creditNote.id,
        orderId: null,
        total: creditNote.totalExVat,
        customerBusinessName: creditNote.SenderBusiness.name,
        type: "creditNote" as const,
        createdAt: creditNote.createdAt,
        paidAt: creditNote.paidAt,
      };
    });
    const customFeeRows = sellarImportPayout.CustomFees.map(
      (customFee: {
        id: number;
        name: string;
        amountExVat: number;
        createdAt: Date;
      }) => {
        return {
          id: customFee.id,
          orderId: null,
          total: customFee.amountExVat,
          customerBusinessName: customFee.name,
          type: "customFee" as const,
          createdAt: customFee.createdAt,
          paidAt: null,
        };
      },
    );

    return [...orderRows, ...creditNoteRows, ...customFeeRows];
  }, [sellarImportPayout]);

  const columns = useMemo(
    () => [
      {
        header: "Order ID",
        accessorKey: "orderId",
        enableSorting: false,
        cell: (info: {
          row: {
            original: TableRow;
          };
          getValue: () => string;
        }) => {
          const orderId = info.getValue();
          if (!orderId) {
            if (info.row.original.type === "customFee") {
              return (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">Custom Fee</span>
                  <AdminOnly>
                    <button
                      type="button"
                      className="flex h-5 w-5 cursor-pointer items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() =>
                        removeCustomFeeMutation.mutate(info.row.original.id)
                      }
                      title="Remove fee"
                      disabled={removeCustomFeeMutation.isPending}
                    >
                      <Icon icon="DELETE" size={10} />
                    </button>
                  </AdminOnly>
                </div>
              );
            }
            return "";
          }
          const link = ROUTES.ORDER_DETAILS.href({ orderId });
          return (
            <Anchor href={link} target="_blank">
              {info.getValue() || ""}
            </Anchor>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created",
        enableSorting: false,
        cell: (info: {
          row: {
            original: TableRow;
          };
          getValue: () => number;
        }) => {
          const createdAt = info.row.original.createdAt;
          return formatDate(createdAt, {
            format: "DD/MM/YY",
          });
        },
      },
      {
        header: "Paid",
        accessorKey: "paid",
        enableSorting: false,
        cell: (info: {
          row: {
            original: TableRow;
          };
          getValue: () => number;
        }) => {
          const paidAt = info.row.original.paidAt;
          if (!paidAt) {
            return "-";
          }
          return formatDate(paidAt, {
            format: "DD/MM/YY",
          });
        },
      },

      {
        header: "Customer",
        accessorKey: "customerBusinessName",
        enableSorting: false,
        cell: (info: { getValue: () => string }) => info.getValue(),
      },
      {
        header: "Price (ex. VAT)",
        accessorKey: "total",
        enableSorting: false,
        cell: (info: {
          row: {
            original: TableRow;
          };
          getValue: () => number;
        }) => {
          const sign = info.row.original.type === "order" ? "" : "-";
          return `${sign}${formatCurrency(info.getValue())}`;
        },
      },
    ],
    [removeCustomFeeMutation],
  );

  if (!sellarImportPayout) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      {/* bread crumb heading */}

      <div className="flex items-center justify-between">
        <H5 className="my-[26px] flex flex-wrap items-center space-x-1 bg-transparent text-base font-medium">
          <div
            className="cursor-pointer hover:underline"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              if (params.get("invoiceId")) {
                navigate(ROUTES.BUSINESS_FINANCE_CREDIT);
              } else {
                params.delete("payout");
                setSearchParams(params);
                onHide();
              }
            }}
          >
            Sellar Import
          </div>
          <Icon icon="CHEVRON_RIGHT" className="m-0" />
          <div>
            {" "}
            Payout {sellarImportPayout.id}{" "}
            <span className="font-light">
              (
              {formatDate(sellarImportPayout.captureStart, {
                format: "DD/MM/YY",
              })}{" "}
              -{" "}
              {formatDate(sellarImportPayout.captureEnd, {
                format: "DD/MM/YY",
              })}
              )
            </span>
          </div>
          <div className="flex flex-col px-2">
            <StatusBadge status={sellarImportPayout.status} />
          </div>
        </H5>

        <AdminOnly>
          <div className="flex gap-2">
            <Button
              color="secondary"
              type="button"
              onClick={() => setAddCustomFeeDialogActive(true)}
            >
              <Icon icon="PLUS" />
              Add Fee
            </Button>
            <Button
              type="button"
              onClick={() => setUpdatePayoutDialogActive(true)}
            >
              <Icon icon="PENCIL" />
              Edit
            </Button>
          </div>
        </AdminOnly>
      </div>

      <div className="inline-flex rounded-lg border border-zinc-300 bg-white py-4">
        <div className="flex flex-col gap-2 border-r px-6">
          <span className="text-xs font-semibold">
            Total order amount (ex. VAT)
          </span>
          <span className="text-xl">
            {formatCurrency(sellarImportPayout.totalRevenueExVat)}
          </span>
        </div>
        <div className="flex flex-col gap-2 px-6">
          <span className="text-xs font-semibold">
            Estimated payout (ex. VAT)
          </span>
          <span className="flex items-center gap-1 text-xl text-zinc-800">
            <Dot color="bg-emerald-800" />
            {formatCurrency(
              sellarImportPayout.totalRevenueExVat -
                sellarImportPayout.totalFeeExVat,
            )}
          </span>
        </div>
      </div>
      <div className="mt-4">
        <SellarImportPayoutDocumentSummary
          sellarImportPayoutId={selectedPayoutId}
          revenueInvoice={sellarImportPayout.RevenueInvoice}
          feeInvoice={sellarImportPayout.FeeInvoice}
        />
      </div>

      <div className="container mx-auto mt-4 mb-4 max-w-[1300px]">
        <TanStackTableSimple
          columns={columns}
          data={data}
          paginationLabel="Results per page"
          gridClasses="grid-cols-[repeat(5,_1fr)]"
        />
      </div>

      <UpdateSellarPayoutDialog
        active={updatePayoutDialogActive}
        onDismiss={() => setUpdatePayoutDialogActive(false)}
        existingPayout={sellarImportPayout}
      />

      <AddCustomFeeDialog
        active={addCustomFeeDialogActive}
        onDismiss={() => setAddCustomFeeDialogActive(false)}
        payoutId={selectedPayoutId}
      />
    </>
  );
}

export default PayoutDetailsTable;
